import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { adminService } from '../../services/api';
import { Card, Spinner, StatCard, TrendChart, Pagination } from '../../components/ui';
import { getApiErrorMessage } from '../../utils/apiErrors';

const REQUEST_PAGE_SIZE = 20;

const STATUS_STYLES = {
  healthy: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  critical: 'bg-red-50 text-red-700 border-red-200',
};

// Mirrors monitoring.models.ErrorEvent's category choices on the backend.
const ERROR_CATEGORIES = ['backend_4xx', 'backend_5xx', 'frontend_runtime', 'frontend_network', 'frontend_resource'];
const ERROR_CATEGORY_META = {
  backend_4xx: { label: 'Backend 4xx', color: '#f59e0b', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  backend_5xx: { label: 'Backend 5xx', color: '#dc2626', className: 'bg-red-50 text-red-700 border-red-200' },
  frontend_runtime: { label: 'Frontend runtime', color: '#9333ea', className: 'bg-purple-50 text-purple-700 border-purple-200' },
  frontend_network: { label: 'Frontend network', color: '#2563eb', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  frontend_resource: { label: 'Frontend resource', color: '#64748b', className: 'bg-gray-100 text-gray-600 border-gray-200' },
};

function formatBytes(value) {
  if (value === null || value === undefined) return '—';
  if (value === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / (1024 ** index)).toFixed(index > 1 ? 1 : 0)} ${units[index]}`;
}

function formatUptime(seconds) {
  if (!Number.isFinite(Number(seconds))) return '—';
  const total = Number(seconds);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function StatusBadge({ status, t }) {
  const normalized = status || 'critical';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[normalized] || STATUS_STYLES.critical}`}>
      <span className={`h-2 w-2 rounded-full ${normalized === 'healthy' ? 'bg-emerald-500' : normalized === 'warning' ? 'bg-amber-500' : 'bg-red-500'}`} />
      {t(normalized.charAt(0).toUpperCase() + normalized.slice(1))}
    </span>
  );
}

function ResourceMeter({ label, value, detail, status }) {
  const percent = Math.max(0, Math.min(Number(value) || 0, 100));
  const color = status === 'critical' ? 'bg-red-500' : status === 'warning' ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3 text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-500 tabular-nums">{percent.toFixed(1)}% · {detail}</span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function ServiceCard({ name, service, t }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-gray-500">{t('Service')}</p>
          <h3 className="mt-1 text-lg font-bold capitalize text-gray-900">{t(name.charAt(0).toUpperCase() + name.slice(1))}</h3>
        </div>
        <StatusBadge status={service.status} t={t} />
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
        {service.latency_ms !== undefined && (
          <div><p className="text-gray-400">{t('Latency')}</p><p className="font-semibold text-gray-800 tabular-nums">{service.latency_ms} ms</p></div>
        )}
        {service.uptime_seconds !== undefined && (
          <div><p className="text-gray-400">{t('Uptime')}</p><p className="font-semibold text-gray-800">{formatUptime(service.uptime_seconds)}</p></div>
        )}
        {service.connections !== undefined && (
          <div><p className="text-gray-400">{t('Connections')}</p><p className="font-semibold text-gray-800">{service.connections}/{service.max_connections}</p></div>
        )}
        {service.connected_clients !== undefined && (
          <div><p className="text-gray-400">{t('Clients')}</p><p className="font-semibold text-gray-800">{service.connected_clients}</p></div>
        )}
        {service.size_bytes !== undefined && (
          <div><p className="text-gray-400">{t('Database size')}</p><p className="font-semibold text-gray-800">{formatBytes(service.size_bytes)}</p></div>
        )}
        {service.used_memory_bytes !== undefined && (
          <div><p className="text-gray-400">{t('Memory')}</p><p className="font-semibold text-gray-800">{formatBytes(service.used_memory_bytes)}</p></div>
        )}
        {service.http_status !== undefined && (
          <div><p className="text-gray-400">HTTP</p><p className="font-semibold text-gray-800">{service.http_status}</p></div>
        )}
        {service.version && (
          <div className="col-span-2"><p className="text-gray-400">{t('Version')}</p><p className="truncate font-semibold text-gray-800" title={service.version}>{service.version}</p></div>
        )}
      </div>
      {service.error && <p className="mt-4 rounded-lg bg-red-50 p-2 text-xs text-red-700 break-words">{service.error}</p>}
    </Card>
  );
}

function ErrorCategoryBadge({ category, t }) {
  const meta = ERROR_CATEGORY_META[category] || ERROR_CATEGORY_META.frontend_resource;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${meta.className}`}>
      {t(meta.label)}
    </span>
  );
}

function ErrorTypeChart({ timeline, period, t }) {
  const maximum = Math.max(1, ...timeline.map((point) => (
    ERROR_CATEGORIES.reduce((sum, category) => sum + Number(point[category] || 0), 0)
  )));
  const hasErrors = timeline.some((point) => ERROR_CATEGORIES.some((category) => point[category]));

  return (
    <div>
      <div className="mb-5 flex flex-wrap gap-x-4 gap-y-2">
        {ERROR_CATEGORIES.map((category) => (
          <span key={category} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: ERROR_CATEGORY_META[category].color }} />
            {t(ERROR_CATEGORY_META[category].label)}
          </span>
        ))}
      </div>
      {!hasErrors ? (
        <div className="flex h-56 items-center justify-center text-sm text-gray-400">{t('No errors recorded')}</div>
      ) : (
        <div className="flex h-64 items-end gap-1 border-b border-gray-200 pt-6" role="img" aria-label={t('Errors segregated by type')}>
          {timeline.map((point, index) => {
            const label = period === '7d'
              ? new Date(point.time).toLocaleDateString([], { month: 'short', day: 'numeric' })
              : new Date(point.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            const values = ERROR_CATEGORIES.map((category) => ({ category, count: Number(point[category] || 0) }));
            const tooltip = values.map(({ category, count }) => `${t(ERROR_CATEGORY_META[category].label)}: ${count}`).join('\n');
            const showLabel = period === '7d' || index === 0 || index === timeline.length - 1 || index % 4 === 0;
            return (
              <div key={point.time} className="group relative flex h-full min-w-0 flex-1 flex-col justify-end" title={`${label}\n${tooltip}`}>
                <div className="flex w-full flex-col-reverse overflow-hidden rounded-t-sm" style={{ height: `${values.reduce((sum, item) => sum + item.count, 0) / maximum * 100}%` }}>
                  {values.map(({ category, count }) => count > 0 && (
                    <div
                      key={category}
                      className="w-full min-h-[2px] transition-opacity group-hover:opacity-80"
                      style={{
                        backgroundColor: ERROR_CATEGORY_META[category].color,
                        height: `${count / values.reduce((sum, item) => sum + item.count, 0) * 100}%`,
                      }}
                    />
                  ))}
                </div>
                {showLabel && <span className="absolute top-full mt-2 whitespace-nowrap text-[10px] text-gray-400">{label}</span>}
              </div>
            );
          })}
        </div>
      )}
      {hasErrors && <div className="h-6" />}
    </div>
  );
}

function alertText(alert, t) {
  const component = t(alert.component.charAt(0).toUpperCase() + alert.component.slice(1));
  if (alert.message_key === 'service_unavailable') {
    return t('{{component}} is unavailable', { component });
  }
  if (alert.message_key === 'resource_usage_high') {
    return t('{{component}} usage is above {{threshold}}%', { component, threshold: alert.threshold });
  }
  return alert.message || component;
}

function AdminMonitoring() {
  const { t } = useTranslation();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  // Monitoring can be expensive on a live server. Load once when the page
  // opens, then stay manual unless the administrator explicitly selects an
  // automatic interval for the current visit.
  const [refreshSeconds, setRefreshSeconds] = useState(0);
  const [requestFilter, setRequestFilter] = useState('');
  const [durationSort, setDurationSort] = useState(null);
  const [requestPage, setRequestPage] = useState(1);

  const loadReport = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const response = await adminService.getMonitoringOverview();
      setReport(response.data || response);
      setError('');
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, t, 'Unable to load monitoring data.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => { loadReport(); }, [loadReport]);
  useEffect(() => {
    if (!refreshSeconds) return undefined;
    const timer = setInterval(() => loadReport(true), refreshSeconds * 1000);
    return () => clearInterval(timer);
  }, [loadReport, refreshSeconds]);

  const requestPoints = useMemo(() => (report?.requests?.timeline || []).map((point) => ({
    label: new Date(point.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    value: point.requests,
  })), [report]);
  const latencyPoints = useMemo(() => (report?.requests?.timeline || []).map((point) => ({
    label: new Date(point.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    value: point.avg_latency_ms,
  })), [report]);
  const filteredRequests = useMemo(() => {
    const query = requestFilter.trim().toLowerCase();
    const requests = report?.requests?.recent_requests || [];
    const filtered = query ? requests.filter((request) => [
      request.method,
      request.path,
      request.status_code,
      request.actor?.name,
      request.actor?.email,
      request.actor?.id,
    ].some((value) => String(value || '').toLowerCase().includes(query))) : requests;
    if (!durationSort) return filtered;
    return [...filtered].sort((left, right) => (
      durationSort === 'desc'
        ? right.duration_ms - left.duration_ms
        : left.duration_ms - right.duration_ms
    ));
  }, [report, requestFilter, durationSort]);

  const toggleDurationSort = () => {
    setDurationSort((current) => current === null ? 'desc' : current === 'desc' ? 'asc' : null);
  };
  const pagedRequests = filteredRequests.slice(
    (requestPage - 1) * REQUEST_PAGE_SIZE,
    requestPage * REQUEST_PAGE_SIZE,
  );

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TopNav title={t('System Monitoring')} subtitle={t('Live infrastructure health and application KPIs')} />
        <main className="flex-1 overflow-y-auto p-4 pb-24 sm:p-6 sm:pb-24">
          <div className="mx-auto max-w-7xl space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {report && <StatusBadge status={report.overall_status} t={t} />}
                {report?.generated_at && (
                  <span className="text-sm text-gray-500">{t('Updated')} {new Date(report.generated_at).toLocaleString()}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <select value={refreshSeconds} onChange={(event) => setRefreshSeconds(Number(event.target.value))} className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                  <option value={0}>{t('Manual refresh')}</option>
                  <option value={10}>{t('Every 10 seconds')}</option>
                  <option value={15}>{t('Every 15 seconds')}</option>
                  <option value={30}>{t('Every 30 seconds')}</option>
                </select>
                <button type="button" onClick={() => loadReport(true)} disabled={refreshing} className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60">
                  {refreshing ? t('Refreshing...') : t('Refresh')}
                </button>
              </div>
            </div>

            {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>}
            {loading && !report ? <div className="flex min-h-[50vh] items-center justify-center"><Spinner /></div> : report && (
              <>
                {report.alerts?.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50">
                    <h2 className="font-bold text-amber-900">{t('Active alerts')}</h2>
                    <div className="mt-3 space-y-2">
                      {report.alerts.map((alert, index) => <p key={`${alert.component}-${index}`} className="text-sm text-amber-800">• {alertText(alert, t)}</p>)}
                    </div>
                  </Card>
                )}

                <section>
                  <h2 className="mb-3 text-lg font-bold text-gray-900">{t('Service health')}</h2>
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {Object.entries(report.services || {}).map(([name, service]) => <ServiceCard key={name} name={name} service={service} t={t} />)}
                  </div>
                </section>

                <section className="grid gap-6 lg:grid-cols-2">
                  <Card>
                    <h2 className="text-lg font-bold text-gray-900">{t('Infrastructure usage')}</h2>
                    <div className="mt-5 space-y-6">
                      <ResourceMeter label={t('CPU')} value={report.runtime.cpu.percent} detail={`${report.runtime.cpu.logical_cores} ${t('cores')}`} status={report.runtime.resource_status.cpu} />
                      <ResourceMeter label={t('Memory')} value={report.runtime.memory.percent} detail={`${formatBytes(report.runtime.memory.used_bytes)} / ${formatBytes(report.runtime.memory.total_bytes)}`} status={report.runtime.resource_status.memory} />
                      <ResourceMeter label={t('Disk')} value={report.runtime.disk.percent} detail={`${formatBytes(report.runtime.disk.used_bytes)} / ${formatBytes(report.runtime.disk.total_bytes)}`} status={report.runtime.resource_status.disk} />
                    </div>
                    <div className="mt-6 grid grid-cols-2 gap-4 border-t border-gray-100 pt-5 text-sm sm:grid-cols-4">
                      <div><p className="text-gray-400">{t('Process memory')}</p><p className="font-semibold">{formatBytes(report.runtime.process.memory_rss_bytes)}</p></div>
                      <div><p className="text-gray-400">{t('Threads')}</p><p className="font-semibold">{report.runtime.process.threads}</p></div>
                      <div><p className="text-gray-400">{t('Network received')}</p><p className="font-semibold">{formatBytes(report.runtime.network.bytes_received)}</p></div>
                      <div><p className="text-gray-400">{t('Network sent')}</p><p className="font-semibold">{formatBytes(report.runtime.network.bytes_sent)}</p></div>
                    </div>
                  </Card>

                  <Card>
                    <h2 className="text-lg font-bold text-gray-900">{t('Application KPIs')}</h2>
                    <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      <StatCard label={t('Users')} value={report.application.users} color="blue" />
                      <StatCard label={t('Active users')} value={report.application.active_users} color="green" />
                      <StatCard label={t('Courses')} value={report.application.courses} color="purple" />
                      <StatCard label={t('Published lessons')} value={report.application.published_lessons} color="yellow" />
                      <StatCard label={t('Active enrollments')} value={report.application.active_enrollments} color="green" />
                      <StatCard label={t('Backend uptime')} value={formatUptime(report.requests.process_uptime_seconds)} color="blue" />
                    </div>
                  </Card>
                </section>

                <section>
                  <h2 className="mb-3 text-lg font-bold text-gray-900">{t('API traffic')}</h2>
                  <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <StatCard label={t('Requests (5 min)')} value={report.requests.requests_last_5m} color="blue" />
                    <StatCard label={t('Requests/min')} value={report.requests.requests_per_minute} color="purple" />
                    <StatCard label={t('Average latency')} value={`${report.requests.average_latency_ms} ms`} color="yellow" />
                    <StatCard label={t('Error rate')} value={`${report.requests.error_rate_percent}%`} color={report.requests.error_rate_percent > 2 ? 'red' : 'green'} />
                  </div>
                  <div className="grid gap-6 lg:grid-cols-2">
                    <Card><h3 className="mb-3 font-bold text-gray-800">{t('Request volume')}</h3><TrendChart points={requestPoints} emptyLabel={t('No request samples yet')} /></Card>
                    <Card><h3 className="mb-3 font-bold text-gray-800">{t('Average response time')}</h3><TrendChart points={latencyPoints} color="#f59e0b" valueSuffix=" ms" emptyLabel={t('No latency samples yet')} /></Card>
                  </div>
                </section>

                <section>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-lg font-bold text-gray-900">{t('Error monitoring')}</h2>
                  </div>
                  <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
                    <StatCard label={t('Errors (24h)')} value={report.errors.totals.last_24h} color={report.errors.totals.last_24h > 0 ? 'yellow' : 'green'} />
                    <StatCard label={t('Errors (7d)')} value={report.errors.totals.last_7d} color={report.errors.totals.last_7d > 0 ? 'yellow' : 'green'} />
                    <StatCard label={t('Server errors (24h)')} value={report.errors.totals.server_24h} color={report.errors.totals.server_24h > 0 ? 'red' : 'green'} />
                    <StatCard label={t('Backend errors (24h)')} value={report.errors.totals.backend_24h} color="purple" />
                    <StatCard label={t('Frontend errors (24h)')} value={report.errors.totals.frontend_24h} color="blue" />
                  </div>
                  <div className="grid gap-6 xl:grid-cols-2">
                    <Card>
                      <h3 className="font-bold text-gray-800">{t('Errors — last 24 hours')}</h3>
                      <p className="mb-3 mt-1 text-sm text-gray-500">{t('Hourly totals, segregated by error type')}</p>
                      <ErrorTypeChart timeline={report.errors.timeline_24h || []} period="24h" t={t} />
                    </Card>
                    <Card>
                      <h3 className="font-bold text-gray-800">{t('Errors — last 7 days')}</h3>
                      <p className="mb-3 mt-1 text-sm text-gray-500">{t('Daily totals, segregated by error type')}</p>
                      <ErrorTypeChart timeline={report.errors.timeline_7d || []} period="7d" t={t} />
                    </Card>
                  </div>
                </section>

                <Card padded={false} className="overflow-hidden">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 p-5">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">{t('Most frequent errors')}</h2>
                      <p className="mt-1 text-sm text-gray-500">{t('Grouped by error signature over the last 7 days')}</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-5 py-3 text-left">{t('Category')}</th>
                          <th className="px-5 py-3 text-left">{t('Code')}</th>
                          <th className="px-5 py-3 text-left">{t('Description')}</th>
                          <th className="px-5 py-3 text-left">{t('Path')}</th>
                          <th className="px-5 py-3 text-right">{t('Count')}</th>
                          <th className="px-5 py-3 text-left">{t('Last seen')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(report.errors.frequent || []).map((item, index) => (
                          <tr key={`${item.code}-${item.path}-${index}`}>
                            <td className="whitespace-nowrap px-5 py-3"><ErrorCategoryBadge category={item.category} t={t} /></td>
                            <td className="whitespace-nowrap px-5 py-3 font-mono text-xs font-semibold text-gray-700">{item.code}</td>
                            <td className="min-w-80 max-w-lg whitespace-normal px-5 py-3 text-gray-600">{item.description}</td>
                            <td className="max-w-xs truncate px-5 py-3 font-mono text-xs text-gray-500" title={item.path}>{item.path || '—'}</td>
                            <td className="whitespace-nowrap px-5 py-3 text-right font-semibold tabular-nums text-gray-700">{item.count}</td>
                            <td className="whitespace-nowrap px-5 py-3 text-gray-500">{new Date(item.last_seen).toLocaleString()}</td>
                          </tr>
                        ))}
                        {!(report.errors.frequent || []).length && (
                          <tr><td colSpan="6" className="px-5 py-10 text-center text-gray-500">{t('No errors recorded')}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card padded={false} className="overflow-hidden">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 p-5">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">{t('Recent API requests')}</h2>
                      <p className="mt-1 text-sm text-gray-500">{t('Requests from all users and anonymous visitors')}</p>
                    </div>
                    <input
                      type="search"
                      value={requestFilter}
                      onChange={(event) => { setRequestFilter(event.target.value); setRequestPage(1); }}
                      placeholder={t('Filter by user, path or method')}
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary-400 sm:w-72"
                    />
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr><th className="px-5 py-3 text-left">{t('Time')}</th><th className="px-5 py-3 text-left">{t('User')}</th><th className="px-5 py-3 text-left">{t('Method')}</th><th className="px-5 py-3 text-left">{t('Path')}</th><th className="px-5 py-3 text-left">{t('Status')}</th><th className="px-5 py-3 text-right" aria-sort={durationSort === 'desc' ? 'descending' : durationSort === 'asc' ? 'ascending' : 'none'}><button type="button" onClick={toggleDurationSort} className="inline-flex items-center gap-1 font-semibold hover:text-primary-700" title={t('Sort by duration')}>{t('Duration')}<span aria-hidden="true" className={durationSort ? 'text-primary-600' : 'text-gray-300'}>{durationSort === 'desc' ? '↓' : durationSort === 'asc' ? '↑' : '↕'}</span></button></th></tr></thead>
                      <tbody className="divide-y divide-gray-100">
                        {pagedRequests.map((request, index) => (
                          <tr key={`${request.timestamp}-${index}`}>
                            <td className="whitespace-nowrap px-5 py-3 text-gray-500">{new Date(request.timestamp).toLocaleTimeString()}</td>
                            <td className="px-5 py-3">
                              {request.actor ? (
                                <div><p className="whitespace-nowrap font-semibold text-gray-700">{request.actor.name}</p><p className="whitespace-nowrap text-xs text-gray-400">{request.actor.email}</p></div>
                              ) : <span className="text-gray-400">{t('Anonymous')}</span>}
                            </td>
                            <td className="px-5 py-3 font-semibold text-gray-700">{request.method}</td>
                            <td className="max-w-md truncate px-5 py-3 font-mono text-xs text-gray-600">{request.path}</td>
                            <td className={`px-5 py-3 font-semibold ${request.status_code >= 500 ? 'text-red-600' : request.status_code >= 400 ? 'text-amber-600' : 'text-emerald-600'}`}>{request.status_code}</td>
                            <td className="whitespace-nowrap px-5 py-3 text-right tabular-nums text-gray-600">{request.duration_ms} ms</td>
                          </tr>
                        ))}
                        {!filteredRequests.length && <tr><td colSpan="6" className="px-5 py-10 text-center text-gray-500">{requestFilter ? t('No matching requests') : t('No request samples yet')}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <Pagination page={requestPage} count={filteredRequests.length} pageSize={REQUEST_PAGE_SIZE} onPageChange={setRequestPage} />
                </Card>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default AdminMonitoring;
